<template>
  <Dialog
    :title="type === 'add' ? '新增知识库' : '修改知识库'"
    :visible.sync="dialogVisible"
    :width="isSecondLevel ? '860px' : '560px'"
    :close-on-click-modal="false"
    :before-close="cancel"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
      <el-form-item label="上级节点" prop="parentId">
        <el-select
          v-model="form.parentId"
          placeholder="根节点"
          style="width: 100%"
          filterable
          :disabled="parentLocked"
          @change="handleParentChange"
        >
          <el-option label="根节点" :value="0" />
          <el-option
            v-for="item in parentOptions"
            :key="item.knowledgeId"
            :label="item.knowledgeName"
            :value="item.knowledgeId"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="名称" prop="knowledgeName">
        <el-input
          v-model.trim="form.knowledgeName"
          maxlength="50"
          show-word-limit
          placeholder="请输入知识库名称"
        />
      </el-form-item>
      <el-form-item label="标题" prop="knowledgeTitle">
        <el-input
          v-model.trim="form.knowledgeTitle"
          maxlength="100"
          show-word-limit
          placeholder="请输入知识库标题"
        />
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number
          v-model="form.sort"
          :min="0"
          :max="9999"
          controls-position="right"
        />
      </el-form-item>
      <el-form-item v-if="isSecondLevel" label="内容" prop="knowledgeContent">
        <RichEditor
          v-if="dialogVisible"
          v-model="form.knowledgeContent"
          height="320px"
          placeholder="请输入知识库内容"
        />
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button plain round size="small" @click="cancel">取 消</el-button>
      <el-button
        plain
        round
        size="small"
        type="primary"
        :loading="submitLoading"
        @click="submitForm"
        >确 定</el-button
      >
    </div>
  </Dialog>
</template>

<script>
import RichEditor from "@/components/RichEditor.vue";
import {
  addKnowledge,
  updateKnowledge,
  getKnowledgeDetail
} from "@/api/content/knowledge";

const defaultForm = () => ({
  knowledgeId: undefined,
  knowledgeName: "",
  knowledgeTitle: "",
  knowledgeContent: "",
  parentId: 0,
  sort: 0
});

function isEmptyHtml(html) {
  const text = String(html || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return !text;
}

export default {
  name: "KnowledgeForm",
  components: { RichEditor },
  data() {
    return {
      dialogVisible: false,
      submitLoading: false,
      type: "add",
      parentLocked: false,
      parentOptions: [],
      form: defaultForm(),
      rules: {
        knowledgeName: [
          { required: true, message: "请输入知识库名称", trigger: "blur" },
          {
            min: 1,
            max: 50,
            message: "名称长度为1~50个字符",
            trigger: "blur"
          }
        ],
        knowledgeTitle: [
          { max: 100, message: "标题不能超过100个字符", trigger: "blur" }
        ],
        parentId: [
          { required: true, message: "请选择上级节点", trigger: "change" }
        ],
        sort: [
          { required: true, message: "请输入排序", trigger: "change" },
          {
            type: "number",
            min: 0,
            max: 9999,
            message: "排序范围为0~9999",
            trigger: "change"
          }
        ],
        knowledgeContent: [
          {
            validator: (rule, value, callback) => {
              if (!this.isSecondLevel) {
                callback();
                return;
              }
              if (isEmptyHtml(value)) {
                callback(new Error("请输入知识库内容"));
                return;
              }
              callback();
            },
            trigger: "change"
          }
        ]
      }
    };
  },
  computed: {
    isSecondLevel() {
      return Number(this.form.parentId) > 0;
    }
  },
  methods: {
    handleParentChange() {
      if (!this.isSecondLevel) {
        this.form.knowledgeContent = "";
      }
      this.$nextTick(() => {
        this.$refs.formRef &&
          this.$refs.formRef.clearValidate(["knowledgeContent"]);
      });
    },
    async open(type, row = {}, parentOptions = []) {
      this.type = type;
      this.parentOptions = parentOptions || [];
      this.parentLocked = !!(row && row.parentLocked);
      this.dialogVisible = true;

      if (type === "edit" && row.knowledgeId != null) {
        try {
          const result = await getKnowledgeDetail(row.knowledgeId);
          const data = (result && result.data) || row;
          this.form = {
            knowledgeId: data.knowledgeId,
            knowledgeName: data.knowledgeName || "",
            knowledgeTitle: data.knowledgeTitle || "",
            knowledgeContent: data.knowledgeContent || "",
            parentId:
              data.parentId != null && data.parentId !== ""
                ? Number(data.parentId)
                : 0,
            sort: Number(data.sort) || 0
          };
        } catch (e) {
          this.cancel();
          return;
        }
      } else {
        this.form = defaultForm();
        if (row && row.parentId != null) {
          this.form.parentId = Number(row.parentId) || 0;
        }
      }

      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate();
      });
    },
    async submitForm() {
      if (this.submitLoading) return;
      try {
        await this.$refs.formRef.validate();
      } catch (e) {
        return;
      }
      this.submitLoading = true;
      const parentId = Number(this.form.parentId) || 0;
      const payload = {
        ...this.form,
        parentId,
        sort: Number(this.form.sort) || 0,
        knowledgeContent: parentId > 0 ? this.form.knowledgeContent || "" : ""
      };
      const request = this.form.knowledgeId ? updateKnowledge : addKnowledge;
      try {
        await request(payload);
        this.$message.success(this.form.knowledgeId ? "修改成功" : "新增成功");
        this.cancel();
        this.$emit("success");
      } catch (e) {
        // interceptor
      } finally {
        this.submitLoading = false;
      }
    },
    cancel() {
      this.dialogVisible = false;
      this.parentLocked = false;
      this.form = defaultForm();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/views/Konwledge/KnowledgeForm.vue